import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type Notification, readNotification } from '@enterprise/api';
import { ensureError, findIndex, isDefined, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { notificationsQueryKeys } from '@feat/notifications/common/notifications-query-keys';
import { useApplicationStore } from '@hooks/application/application-store.hook';
import { useNotifier } from '@hooks/notifier/notifier.hook';

import type { NotificationsCacheContext } from './types/notifications-cache-context.type';
import type { ReadNotificationArguments } from './types/read-notification-arguments.type';

export const useReadNotification = () => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });

  const queryClient = useQueryClient();
  const notifier = useNotifier();
  const listKey = notificationsQueryKeys.list();

  return useMutation<unknown, Error, ReadNotificationArguments, NotificationsCacheContext>({
    mutationFn: ({ id }) => {
      return readNotification(enterpriseApi, id);
    },
    onError: (e, _vars, context) => {
      if (isDefined(context)) {
        queryClient.setQueryData(listKey, context.snapshot);
      }

      notifier.error({ description: ensureError(e).message, title: 'Failed to mark as read' });
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const snapshot = queryClient.getQueryData<OperationResultWithData<Notification[]>>(listKey);

      queryClient.setQueryData<OperationResultWithData<Notification[]>>(listKey, (cached) => {
        if (isNotDefined(cached)) {
          return cached;
        }

        if (cached.failure) {
          return cached;
        }

        const { data } = cached;

        if (isNotDefined(data)) {
          return cached;
        }

        const index = findIndex(data, 'id', id);

        if (index < 0) {
          return cached;
        }

        const updated = data.map((item, i) => {
          if (i !== index) {
            return item;
          }

          return { ...item, readDate: new Date().toISOString() };
        });

        return { ...cached, data: updated };
      });

      return { snapshot };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
};
